import React, { useCallback, useMemo, useState } from 'react';
import DataEditor, { GridCellKind } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  ArrowDownTrayIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  DEFAULT_SETTINGS,
  PAY_RANGES,
  SEGMENT_LENGTH_FT,
  calculateNebraskaPayFactor,
  fmtNebraskaRange,
} from '../../../utils/pay/nebraskaPayCalculator';

// =============================================================================
// CONFIG - Nebraska AC Pavement Smoothness Pay Factor
// =============================================================================

// =============================================================================
// HELPERS
// =============================================================================

const cn = (...c) => c.filter(Boolean).join(' ');

const fmtStation = (ft) => {
  if (ft == null) return '--';
  const hundreds = Math.floor(ft / 100);
  const remainder = Math.round(ft % 100);
  return `${hundreds}+${String(remainder).padStart(2, '0')}`;
};

const fmtCurrency = (v) => {
  const formatted = Math.abs(v).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `-${formatted}` : formatted;
};

const fmtExcelNumber = (value, digits = 2) => {
  if (!Number.isFinite(value)) return '';
  return value.toFixed(digits);
};

const getPayFactorClass = (payFactor) => {
  if (payFactor >= 1.03) return 'bg-green-100 text-green-700';
  if (payFactor > 1.00) return 'bg-lime-100 text-lime-700';
  if (payFactor === 1.00) return 'bg-blue-100 text-blue-700';
  if (payFactor >= 0.96) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};

const SummaryCard = ({ label, value, unit, className = '', valueClassName = '' }) => (
  <div className={cn('bg-gray-50 border border-gray-200 rounded-sm px-3 py-2', className)}>
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={cn('text-xl font-bold text-gray-900', valueClassName)}>
      {value}
      {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
    </div>
  </div>
);

// =============================================================================
// PAY TABLE MODAL
// =============================================================================

const PayTableModal = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} className="relative z-50">
    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
    <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
      <DialogPanel className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
          <DialogTitle className="text-base font-semibold text-gray-900">
            Nebraska Smoothness Pay Factor Table
          </DialogTitle>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <table className="w-full text-sm border border-gray-200">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left border-b">Average IRI (in/mi)</th>
                <th className="px-3 py-2 text-left border-b">Pay Factor</th>
                <th className="px-3 py-2 text-left border-b">Deduction</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {PAY_RANGES.map(row => (
                <tr key={row.code} className={row.deductionRate > 0 ? 'bg-red-50' : ''}>
                  <td className="px-3 py-2 font-semibold text-gray-900">{fmtNebraskaRange(row)}</td>
                  <td className="px-3 py-2">
                    <span className={cn('inline-block px-2 py-0.5 rounded-sm text-xs font-bold', getPayFactorClass(row.payFactor))}>
                      {row.payFactor.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-700">
                    {row.deductionRate > 0 ? `${fmtCurrency(row.deductionRate)} per ${SEGMENT_LENGTH_FT} ft` : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="p-3 bg-gray-50 rounded-sm text-xs text-gray-600 space-y-1">
            <p className="font-semibold">Reference</p>
            <p>AC Pavement Smoothness Pay Factor workbook reimplementation.</p>
            <p>IRI range boundaries are lower-bound inclusive and upper-bound exclusive. A value of 43.0 falls in 43-49.</p>
            <p>Overall pay factor is length-weighted across all non-exception segments. Deduction is separate from the pay factor.</p>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function NebraskaPayAdjustment({ project, segments, isReversed = false }) {
  const [settings] = useState({ ...DEFAULT_SETTINGS, ...project?.payAdjustment });
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  const [copiedExcelRows, setCopiedExcelRows] = useState(false);

  const data = useMemo(
    () => calculateNebraskaPayFactor(segments, settings, isReversed),
    [segments, settings, isReversed]
  );

  const displayRows = useMemo(() => {
    if (!data) return [];
    return issuesOnly ? data.rows.filter(row => row.hasIssue) : data.rows;
  }, [data, issuesOnly]);

  const segmentGridColumns = useMemo(() => {
    const dataColumns = [
      { id: 'num', title: '#', width: 70 },
      { id: 'startStation', title: 'Beginning Sta.', width: 140 },
      { id: 'stopStation', title: 'Ending Sta.', width: 140 },
      { id: 'lengthFt', title: 'Length (ft)', width: 120 },
      { id: 'avgIri', title: 'Avg IRI', width: 110 },
      { id: 'rangeLabel', title: 'IRI Range', width: 115 },
      { id: 'payFactor', title: 'Pay Factor', width: 115 },
      { id: 'deduction', title: 'Deduction', width: 130 },
    ];
    const fillerColumns = Array.from({ length: 8 }, (_, index) => ({
      id: `blank-${index + 1}`,
      title: '',
      width: 110,
      grow: 1,
    }));

    return [...dataColumns, ...fillerColumns];
  }, []);

  const segmentGridRows = useMemo(() => displayRows.map(row => ({
    num: String(row.num),
    startStation: fmtStation(row.startStation),
    stopStation: fmtStation(row.stopStation),
    lengthFt: row.lengthFt.toFixed(0),
    avgIri: row.avgIri.toFixed(1),
    rangeLabel: row.rangeLabel,
    payFactor: row.payFactor.toFixed(2),
    deduction: row.deductionDollars > 0 ? fmtCurrency(row.deductionDollars) : '',
    hasIssue: row.hasIssue,
  })), [displayRows]);

  const totalGridRow = useMemo(() => data ? {
    num: '',
    startStation: '',
    stopStation: 'Total:',
    lengthFt: data.totalLengthFt.toFixed(0),
    avgIri: `Avg: ${data.avgIri.toFixed(1)}`,
    rangeLabel: '',
    payFactor: data.overallPayFactor.toFixed(4),
    deduction: data.totalDeductionDollars > 0 ? fmtCurrency(data.totalDeductionDollars) : '',
    hasIssue: data.totalDeductionDollars > 0,
    isTotal: true,
  } : null, [data]);

  const gridRowsWithTotal = useMemo(() => (
    totalGridRow ? [...segmentGridRows, totalGridRow] : segmentGridRows
  ), [segmentGridRows, totalGridRow]);

  const getSegmentCellContent = useCallback(([col, row]) => {
    const gridRow = gridRowsWithTotal[row];
    const column = segmentGridColumns[col];
    const value = gridRow?.[column?.id] ?? '';

    let themeOverride;
    if (gridRow?.isTotal) {
      themeOverride = {
        bgCell: '#f3f4f6',
        bgCellMedium: '#e5e7eb',
        textDark: gridRow.hasIssue && column?.id === 'deduction' ? '#b91c1c' : '#111827',
      };
    } else if (gridRow?.hasIssue) {
      themeOverride = {
        bgCell: '#fef2f2',
        bgCellMedium: '#fee2e2',
        textDark: column?.id === 'avgIri' || column?.id === 'deduction' ? '#b91c1c' : '#111827',
      };
    }

    return {
      kind: GridCellKind.Text,
      data: String(value),
      displayData: String(value),
      allowOverlay: false,
      readonly: true,
      themeOverride,
    };
  }, [gridRowsWithTotal, segmentGridColumns]);

  const exportCSV = () => {
    const headers = [
      'Segment',
      'Beginning Sta.',
      'Ending Sta.',
      'Length (ft)',
      'Average IRI (in/mi)',
      'IRI Range',
      'Pay Factor',
      'Deduction',
    ].join(',');

    const csvRows = displayRows.map(row => [
      row.num,
      row.startStation.toFixed(0),
      row.stopStation.toFixed(0),
      row.lengthFt.toFixed(2),
      row.avgIri.toFixed(2),
      row.rangeLabel,
      row.payFactor.toFixed(4),
      row.deductionDollars.toFixed(2),
    ].join(','));

    const blob = new Blob([[headers, ...csvRows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `nebraska-smoothness-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const copyExcelRows = async () => {
    if (!data?.rows?.length) return;

    const tsv = data.rows.map(row => [
      fmtExcelNumber(row.startStation, 0),
      fmtExcelNumber(row.stopStation, 0),
      fmtExcelNumber(row.lengthFt, 2),
      fmtExcelNumber(row.leftIri, 2),
      fmtExcelNumber(row.rightIri, 2),
    ].join('\t')).join('\n');

    try {
      await navigator.clipboard.writeText(tsv);
      setCopiedExcelRows(true);
      setTimeout(() => setCopiedExcelRows(false), 1500);
    } catch (err) {
      console.error('Failed to copy segment rows:', err);
    }
  };

  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <PayTableModal open={showPayTable} onClose={() => setShowPayTable(false)} />

      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 bg-gray-50">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Nebraska Smoothness Pay Factor</h3>
          <p className="text-xs text-gray-500">AC pavement smoothness workbook</p>
        </div>

        <div className="flex-1" />

        {data?.deductionSegmentCount > 0 && (
          <div
            className="h-8 px-2.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-sm flex items-center gap-1.5 whitespace-nowrap"
            title={`${data.deductionSegmentCount} segment${data.deductionSegmentCount > 1 ? 's' : ''} in IRI range 96-105 or 105+ have corrective-work deductions.`}
          >
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
            {data.deductionSegmentCount} corrective deduction{data.deductionSegmentCount > 1 ? 's' : ''}
          </div>
        )}

        <button
          onClick={() => setShowPayTable(true)}
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Table
        </button>

        <button
          onClick={copyExcelRows}
          disabled={!data?.rows?.length}
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {copiedExcelRows ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4" />
          )}
          {copiedExcelRows ? 'Copied' : 'Copy Excel Rows'}
        </button>

        <button
          onClick={exportCSV}
          disabled={!displayRows.length}
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>

      {data ? (
        <div className="px-4 py-4 border-b border-gray-200 space-y-3">
          <div className="grid grid-cols-5 gap-3">
            <SummaryCard
              label="Pay Factor"
              value={data.overallPayFactor.toFixed(4)}
              valueClassName={cn(
                data.overallPayFactor > 1 && 'text-green-700',
                data.overallPayFactor < 1 && 'text-red-700'
              )}
            />
            <SummaryCard label="Pay Percent" value={`${(data.overallPayFactor * 100).toFixed(2)}%`} />
            <SummaryCard
              label="Deduction"
              value={fmtCurrency(data.totalDeductionDollars)}
              valueClassName={data.totalDeductionDollars > 0 ? 'text-red-700' : ''}
            />
            <SummaryCard label="Avg IRI" value={data.avgIri.toFixed(1)} unit="in/mi" />
            <SummaryCard label="Total Length" value={data.totalLengthFt.toFixed(0)} unit="ft" />
          </div>

          {data.lengthMismatchCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 px-3 py-2 rounded-sm">
              <ExclamationTriangleIcon className="h-4 w-4" />
              {data.lengthMismatchCount} segment{data.lengthMismatchCount > 1 ? 's' : ''} use an explicit length that differs from station distance.
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">
          No segment data available
        </div>
      )}

      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Segments {data && <span className="text-gray-400 font-normal">({data.rows.length})</span>}
        </span>
        {data?.deductionSegmentCount > 0 && (
          <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={issuesOnly}
              onChange={e => setIssuesOnly(e.target.checked)}
              className="w-3.5 h-3.5 text-blue-600 rounded-sm"
            />
            Deductions only ({data.deductionSegmentCount})
          </label>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {displayRows.length > 0 ? (
          <DataEditor
            columns={segmentGridColumns}
            rows={gridRowsWithTotal.length}
            getCellContent={getSegmentCellContent}
            getCellsForSelection={true}
            rowMarkers="none"
            rowHeight={34}
            headerHeight={38}
            smoothScrollX={true}
            smoothScrollY={true}
            rangeSelect="multi-rect"
            freezeColumns={1}
            theme={{
              accentColor: '#2563eb',
              accentLight: 'rgba(37, 99, 235, 0.12)',
              fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
              cellHorizontalPadding: 12,
              headerFontStyle: '600 12px',
              baseFontStyle: '13px',
            }}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 py-12">
            <div className="text-center">
              <XCircleIcon className="h-10 w-10 mx-auto mb-2" />
              <p>{issuesOnly ? 'No segments with deductions' : 'No segment data available'}</p>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span>Nebraska AC Pavement Smoothness Pay Factor</span>
          {isReversed && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-sm">Reverse Stationing</span>
          )}
        </div>
        <span>Segment basis: {SEGMENT_LENGTH_FT} ft</span>
      </div>
    </div>
  );
}
